<template>
    <div v-if="project.type === 'caseStudy' && displayRelatedFooter && footerProjects.length"
         class="footer-section ufo-sec related-case-studies"
         :class="[cornerRadius, relatedAlignment, relatedSizeClass]"
         :style="[{'--section-padding-top' :( relatedFooter.style.paddingTop ? relatedFooter.style.paddingTop + 'px' : null),
          '--section-padding-bottom' : relatedFooter.style.paddingBottom ? relatedFooter.style.paddingBottom + 'px' : null,
           }]"
    >
        <ToggleGrid />
        <SectionBackground v-if="relatedFooter" :section="relatedFooter" />
        <div class="grid-wrapper">
            <div class="grid">
                <div class="grid-container">
                    <div class="related-projects-container" :class="{'no-related-header': !showRelatedTitle && !showRelatedDividerLine}">
                        <AnimationWrapper>
                            <template #animationContent>
                                <div class="related-title" v-if="showRelatedTitle">
                                    <Title :section="relatedFooter" />
                                </div>
                            </template>
                        </AnimationWrapper>
                        <div v-if="showRelatedDividerLine" class="separator related-divider">
                            <AnimationWrapper is-final-section>
                                <template #animationContent>
                                    <div class="line" :class="relatedLineClasses"></div>
                                </template>
                            </AnimationWrapper>
                        </div>
                        <div class="related-projects">
                            <div v-bind:key="footerProject" v-for="(footerProject, index) in footerProjects" @click="navigateToProjectFooter(footerProject.shortId)" class="related-project">
                                <AnimationWrapper :delay-animation="index ? index * 0.1 : 0">
                                    <template #animationContent>
                                        <div class="image">
                                            <ProjectThumbnail v-if="footerProject.settings.usingGeneratedThumbnail" :caseStudy="footerProject" force-squared />
                                            <img v-else-if="footerProject.image.url"
                                                 loading="lazy"
                                                 :src="footerProject.image.croppedImageUrl ? footerProject.image.croppedImageUrl : footerProject.image.url"
                                                 :alt="footerProject.name">
                                            <img v-else-if="footerProject.sections[0].type === 'MainHeader' && footerProject.sections[0].image && footerProject.sections[0].image.url" loading="lazy" :src="footerProject.sections[0].image.croppedImageUrl ? footerProject.sections[0].image.croppedImageUrl : footerProject.sections[0].image.url" :alt="footerProject.name">
                                            <img v-else-if="!footerProject.sections[0].backgroundColor" loading="lazy" src="/example_project_images/v2/empty-project-thumbnail.jpg" :alt="footerProject.name">
                                        </div>
                                    </template>
                                </AnimationWrapper>
                                <AnimationWrapper :delay-animation="index ? index * 0.1 : 0">
                                    <template #animationContent>
                                        <div class="related-project-title">
                                            <h4>{{ getProjectName(footerProject) }}</h4>
                                        </div>
                                        <div v-if="showRelatedDescription" class="related-project-subtitle">
                                            <p>{{ getProjectSubtitle(footerProject) }}</p>
                                        </div>
                                    </template>
                                </AnimationWrapper>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ToggleGrid from "../../../common/components/ToggleGrid.vue";
import ProjectThumbnail from "../../../sections/projectGrid/ProjectThumbnail.vue";
import AnimationWrapper from "../../../common/components/AnimationWrapper.vue";
import Title from "@/scripts/view/portfolio/sections/modules/Title.vue";
import SectionBackground from "@/scripts/sections/common/SectionBackground.vue";
import {
    convertRichTextHTMLToPlainText
} from "@/scripts/common/components/textEditor/utilities/textConverterUtilities.js";
import {updateSectionLevelColorCssInHead} from "@/scripts/common/TextStyleRenderHelper.js";
import {CaseStudyCardSizeEnum, FooterCornerRadiusMap} from "@/scripts/common/enums.js";
import {getRelatedCaseStudies} from "@/scripts/common/utilities/sectionHelpers.js";
import {mapGetters} from "vuex";
import {getPortfolioGenerationPreviewPath} from "@/scripts/view/portfolio/generationPreview.js";

export default {
    name: "RelatedCaseStudiesSection",
    components: {
        SectionBackground,
        AnimationWrapper,
        Title,
        ToggleGrid,
        ProjectThumbnail
    },
    mounted() {
        if(!this.$route.query.templatePreview) {
            if (this.relatedFooter) {
                updateSectionLevelColorCssInHead(this.relatedFooter);
            }
        }
    },
    computed: {
        ...mapGetters("view", ["viewData", "caseStudyRoute"]),
        ...mapGetters("design", ["templateSettings"]),
        cornerRadius() {
            const cardStyle = this.templateSettings?.cardStyle || {};
            const value = cardStyle.cornerRadiusPx ?? 0;
            const footerRadiusPx = FooterCornerRadiusMap[value] ?? 0;
            return footerRadiusPx ? `corner-radius-footer-${footerRadiusPx}` : '';
        },
        portfolio() {
            return this.viewData.portfolio
        },
        project() {
            return this.viewData.project
        },
        footerProjects() {
            return getRelatedCaseStudies({
                caseStudies: this.viewData.caseStudies,
                currentId: this.viewData.activePage
            });
        },
        displayRelatedFooter() {
            return !!this.relatedFooterVisible;
        },
        relatedFooter() {
            return this.portfolio.relatedCaseStudyFooter;
        },
        relatedFooterVisible() {
            if (this.project.type !== 'caseStudy') {
                return false;
            }
            return !!this.portfolio.settings?.showRelatedCaseStudiesOnProjects;
        },
        relatedAlignment() {
            return this.relatedFooter.styleSettings.boxAlign;
        },
        relatedSizeClass() {
            const size = this.relatedFooter.cardSize;
            return size === CaseStudyCardSizeEnum.SMALL ? "related-size-small" : "related-size-medium";
        },
        relatedLineClasses() {
            return `${this.relatedFooter.lineWeight}`
        },
        showRelatedDividerLine() {
            return !!this.relatedFooter.displayDividerLine;
        },
        showRelatedTitle() {
            return !!this.relatedFooter.displayTitle;
        },
        showRelatedDescription() {
            const relatedFooterDescriptionVisible = this.relatedFooter.displayDescription !== false;
            const homeGridDescriptionHidden = this.templateSettings?.projectTitle?.hideDescription === true;
            return relatedFooterDescriptionVisible && !homeGridDescriptionHidden;
        }
    },
    methods: {
        navigateToProjectFooter(projectShortId) {
            const caseStudyRoute = getPortfolioGenerationPreviewPath(this.$route, projectShortId)
                || this.caseStudyRoute(projectShortId);
            if (!caseStudyRoute) return;
            this.$router.push(caseStudyRoute).then(() => {
                window.scrollTo(0, 0);
            });
        },
        getProjectName(project) {
            let headerTitle = project.sections[0];
            return project.name !== null ? project.name : convertRichTextHTMLToPlainText(headerTitle.title.text)
        },
        getProjectSubtitle(project) {
            let headerTitle = project.sections[0];
            if (project.subtitle != null && project.subtitle !== '') {
                return project.subtitle;
            }
            return convertRichTextHTMLToPlainText(headerTitle?.subtitle?.text || '');
        },
    }
}
</script>
